import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
// Sólo el tipo (se borra al compilar): la forma la manda el componente que arma la cuenta, no al
// revés — el servicio no sabe nada de cómo se resuelve el banco, sólo de qué forma viaja.
import type { CuentaBancariaComercio } from 'src/app/components/altas/cuenta-bancaria/cuenta-bancaria.component';
import type { PuestaEnMarcha } from 'src/app/components/altas/puesta-en-marcha/puesta-en-marcha.component';

/**
 * Las solicitudes que corren por un workflow — `/api/workflows/solicitudes` del núcleo
 * (`modules/workflow/http/rutas-solicitudes.js`). Primer tipo: el alta de comercio (ALTA-000001).
 *
 * 🔴 CONTRATO: las formas de abajo están copiadas campo por campo de `aPantalla`, `resumen` e
 * `historial` de `modules/workflow/infra/repos/solicitudes.js`. Si el núcleo cambia, esto cambia.
 * Todo responde `{ ok, data }`; los errores traen `{ ok:false, error, msg, detalle? }`.
 */
export type EstadoSolicitud = 'EN_CURSO' | 'APROBADA' | 'RECHAZADA';
export type Decision = 'APROBAR' | 'DEVOLVER' | 'RECHAZAR';
// VOCABULARIO.md, 2026-09-29: «Pagos» se llama «Finanzas». PAGOS sigue existiendo SOLO para leer
// contactos de solicitudes de antes del cambio — nunca se manda de nuevo.
export type PapelContacto = 'ADMINISTRADOR' | 'FINANZAS' | 'TECNICO' | 'OTRO';
type PapelContactoLegado = 'PAGOS';

export interface ContactoAlta {
  /** Legado: como venía ANTES de «un contacto, varios papeles» (mismo día). Sólo lectura — de acá
   *  no se manda más; usar `papelesDe()` para leerlo junto con `papeles`. */
  papel?: PapelContacto | PapelContactoLegado | null;
  /** Lo que se manda y se lee ahora: un contacto puede tener varios papeles a la vez. */
  papeles?: Array<PapelContacto | PapelContactoLegado>;
  nombre: string; correo?: string | null; telefono?: string | null; whatsapp?: string | null;
}

/** Los papeles de un contacto, ya normalizados: si sólo trae el `papel` viejo, se lee como
 *  `[papel]`; PAGOS se traduce a FINANZAS; sin duplicados. Único punto de esta regla — todo lo
 *  demás (validar, mostrar, contar) pasa por acá para no repetirla en cada pantalla. */
export function papelesDe(c: { papel?: string | null; papeles?: string[] | null }): PapelContacto[] {
  const crudos = (c.papeles && c.papeles.length) ? c.papeles : (c.papel ? [c.papel] : []);
  const normalizados = crudos.map((p) => (p === 'PAGOS' ? 'FINANZAS' : p) as PapelContacto);
  return normalizados.filter((p, i) => normalizados.indexOf(p) === i);
}

/** «Faltan: el contacto administrador» — el único papel obligatorio de entrada. El de finanzas dejó
 *  de frenar (Juan, 2026-10-14, o25 de Adrián: el administrador puede hacer todo; sin finanzas, sus
 *  avisos van al administrador). El técnico depende de las condiciones (Integración / Tienda en línea), que acá todavía
 *  no se saben: por eso NO frena — es un aviso aparte, no una falta. `null` si no falta ninguno. */
export function textoFaltanContactos(contactos: Array<{ papel?: string | null; papeles?: string[] | null }>): string | null {
  const todos = contactos.flatMap((c) => papelesDe(c));
  const faltan: string[] = [];
  if (!todos.includes('ADMINISTRADOR')) faltan.push('el contacto administrador');
  if (!faltan.length) return null;
  const texto = faltan.length === 1 ? faltan[0] : `${faltan.slice(0, -1).join(', ')} y ${faltan[faltan.length - 1]}`;
  return `Faltan: ${texto}.`;
}

/** Las piezas del domicilio fiscal (las mismas de la ficha del comercio). */
export interface DomicilioFiscal { street?: string; city?: string; municipality?: string; state?: string; postal_code?: string; }

/** Rótulos del portal del comercio, en el orden en que se muestran. */
export const PIEZAS_FISCAL: Array<{ clave: keyof DomicilioFiscal; rotulo: string; obligatorio: boolean }> = [
  { clave: 'street', rotulo: 'Calle, avenida, edificio, local', obligatorio: true },
  { clave: 'city', rotulo: 'Ciudad', obligatorio: true },
  { clave: 'municipality', rotulo: 'Municipio', obligatorio: false },
  { clave: 'state', rotulo: 'Estado', obligatorio: false },
  { clave: 'postal_code', rotulo: 'Código postal', obligatorio: false },
];

/** El domicilio de una solicitud en sus cinco piezas. Las solicitudes viejas traen solo `direccion_fiscal`: va como calle. */
export const fiscalDe = (d: { fiscal?: DomicilioFiscal; direccion_fiscal?: string } | null | undefined): Required<DomicilioFiscal> => {
  const f = d?.fiscal || {};
  return {
    street: String(f.street || '').trim() ? String(f.street) : String(d?.direccion_fiscal || ''),
    city: f.city || '', municipality: f.municipality || '', state: f.state || '', postal_code: f.postal_code || '',
  };
};

/** Solo las piezas con valor, recortadas (lo que se manda como `fiscal`). */
export const fiscalSinVacios = (f: DomicilioFiscal): DomicilioFiscal => {
  const r: DomicilioFiscal = {};
  for (const p of PIEZAS_FISCAL) { const v = String(f[p.clave] ?? '').trim(); if (v) r[p.clave] = v; }
  return r;
};

export interface DatosAlta {
  // URL propia: las siglas del código del comercio (2–5 letras A-Z) y el acrónimo de la dirección del portal.
  siglas?: string; acronimo?: string;
  nombre_comercial?: string; razon_social?: string; direccion_fiscal?: string; rif?: string;
  // Domicilio fiscal en las mismas piezas que la ficha del comercio. El núcleo arma solo `direccion_fiscal` (texto plano).
  fiscal?: DomicilioFiscal;
  descripcion?: string; tipo_persona?: string; telefono?: string; correo?: string;
  // CONTRATO-PUESTA-EN-MARCHA.md, «C»: la cuenta bancaria principal, la llena el ejecutivo. Al
  // aprobar pasa a `payment_accounts` como la principal. `banco`/`banco_nombre` los resuelve
  // `app-cuenta-bancaria-form` del catálogo de bancos; acá viajan tal cual los devuelve.
  cuenta?: CuentaBancariaComercio | null;
  // Juan, 2026-10-08: los centros de beneficio del comercio (códigos del catálogo; al menos uno para
  // que el ejecutivo apruebe). Al aprobar pasan al comercio como `profit_centers`.
  centros_beneficio?: string[];
  // Juan, 2026-10-08: la primera tienda y su caja, las define el ejecutivo; la puesta en marcha crea ésas.
  tienda?: TiendaInicial | null;
}

/** La primera tienda del comercio: código, nombre y caja obligatorios; dirección y ciudad si se saben. */
export interface TiendaInicial {
  codigo: string; nombre: string; caja: number | null; direccion?: string; ciudad?: string; codigo_sugerido?: boolean;
  // Juan, 2026-10-19: quién va a cobrar en la caja y el gerente de la tienda (nombre y correo). El núcleo los valida;
  // el usuario del portal lo arma el servidor (acá sólo se propone en pantalla).
  // Juan, 2026-10-19: ya no hay cajero (la caja es un usuario, no una persona); el núcleo ignora el de altas viejas.
  gerente?: PersonaTienda | null;
  // El vendedor (nombre y correo): opcional en la carga; el núcleo lo exige si el comercio vende por link de pago.
  vendedor?: PersonaTienda | null;
}

export interface PersonaTienda { nombre: string; correo: string; }

/** Un usuario del portal nacido con el alta (`GET /:id/accesos`), con a quién se le mandó la clave y si ya entró. */
export interface AccesoAlta {
  usuario_id: string; username: string; papel: 'ADMIN' | 'GERENTE' | 'CAJA' | 'VENDEDOR';
  nombre: string; correo: string; enviado_en: string | null; envio_estado: EnvioAcceso; entro: boolean; activo: boolean;
}

/** En qué quedó el correo con la clave: `apagado` = el ambiente no manda correos (la clave va sólo en el PDF). */
export type EnvioAcceso = 'enviado' | 'fallo' | 'apagado' | null;

/** `GET /:id/accesos`: los accesos, si este usuario puede renovarlos y si el ambiente manda correos. */
export interface AccesosAlta { accesos: AccesoAlta[]; puede_renovar: boolean; }

/** Un centro de beneficio del catálogo del núcleo (`GET /centros-beneficio`). */
export interface CentroBeneficio { codigo: string; nombre: string; }

/** El mismo criterio del núcleo (`errorDeTelefono`): 11 dígitos, celular 0412/0414/0416/0422/0424/0426
 *  o fijo 02xx; se acepta +58. Vacío no es error (que sea obligatorio lo dice otra regla). */
export function telefonoInvalido(t: string | null | undefined): boolean {
  const crudo = String(t || '').trim();
  if (!crudo) return false;
  let n = crudo.replace(/\D/g, '');
  if (n.length === 12 && n.startsWith('58')) n = `0${n.slice(2)}`;
  return !/^0(?:2\d{2}|4(?:12|14|16|22|24|26))\d{7}$/.test(n);
}
export const AYUDA_TELEFONO = '11 dígitos: un celular (0412, 0414, 0416, 0422, 0424, 0426) o un fijo (02xx). Por ejemplo 0412-5550101.';

export interface Acciones {
  tomar: boolean; soltar: boolean; comentar: boolean; editar: boolean;
  aprobar: boolean; devolver: boolean; rechazar: boolean;
  // Contrato CONTRATO-ALTA-AUTOMATICA.md §2: aprobada con el alta en FALLO — solo el perfil del
  // aprobador final, o `config.workflows.update`, puede volver a correr `ejecutarAlta`.
  reintentar_alta: boolean;
  // CONTRATO-PUESTA-EN-MARCHA.md, «B»: `puesta_en_marcha.estado === 'FALLO'` — solo el aprobador
  // final, el administrador de altas o `config.workflows.update`. Rehace SOLO lo que falta.
  reintentar_puesta_en_marcha: boolean;
  // CONTRATO-PUESTA-EN-MARCHA.md, «K»: aprobador final, Administrador de altas o
  // config.workflows.update. Se arman AL ENTREGAR y no se guardan; volver a entregar RENUEVA
  // las claves — las que se entregaron antes dejan de servir.
  entregar_credenciales: boolean;
  /** Hay credenciales de integración (el comercio usa un integrador): uso interno de CrediTotal. */
  credenciales_integracion?: boolean;
  motivo: string | null;   // por qué NO puede (texto listo para la pantalla), o null
}

/** Si llegó a la etapa DEVUELTA desde más adelante: quién la devolvió. */
export interface DevueltaPor { rol_nombre: string; username: string; }

export interface EtapaRuta { orden: number; rol_nombre: string; puede_rechazar: boolean; devuelve_a: number | null; estado: 'HECHA' | 'ACTUAL' | 'PENDIENTE' | 'NO_LLEGO' | 'RECHAZADA'; }

export interface Solicitud {
  _id: string; numero: string; tipo_solicitud: string; tipo_nombre: string;
  // Juan, 2026-10-08: por dónde va a vender (fuera del contrato) y, en el último perfil, desde cuándo opera.
  siglas_sugeridas?: string | null; acronimo_sugerido?: string | null;   // URL propia
  codigo_previsto?: string | null;   // código del comercio para proponer el de la tienda (ej. «CM»)
  canales?: string[]; pide_rige_desde?: boolean; rige_desde?: string | null;
  workflow: string; workflow_version: number; estado: EstadoSolicitud;
  etapa: { orden: number; rol: string; rol_codigo: string; rol_nombre: string; desde: string; devuelta_por: DevueltaPor | null } | null;
  responsable: { user: string; username: string; desde: string } | null;
  enviada_por: { username: string; desde: string } | null;
  es_mia: boolean;
  mis_perfiles: string[];      // los perfiles de ESTE workflow que tiene quien mira
  actuo_como: string | null;   // el perfil de la etapa, si quien mira lo tiene (o si es administrador)
  // CONTRATO-ADMINISTRADOR.md: quien mira tiene el rol «Administrador de altas» — actúa, en la etapa
  // actual, como si tuviera el perfil de ella (`actuo_como`). La pantalla dice «Actúas como <perfil>
  // (administrador)» en vez del perfil a secas, y no cambia qué botones se ven (esos ya vienen en
  // `acciones`/`secciones_editables`, calculados igual para él).
  es_admin: boolean;
  tarea: string | null;        // lo que le toca al perfil de la etapa (del tipo de solicitud)
  ruta: { etapas: EtapaRuta[]; fin: string; fin_estado: 'HECHA' | 'RECHAZADA' | 'PENDIENTE' };
  datos: DatosAlta; contactos: ContactoAlta[]; origen: 'CONSOLA' | 'WEB' | 'WHATSAPP';
  // Las condiciones comerciales que carga el Gerente comercial (contrato CONTRATO-CONDICIONES.md,
  // «Las condiciones en el alta»): la MISMA forma con la que se crea una versión de
  // `merchant_terms` (sin `merchant` ni código de versión) — `version: { name, date_init }`,
  // `commission`, `risk_model`, `settlement` (un solo plazo en `rates`), `flat_fee`, `charges`,
  // `modelos`, `contract_reference`. `any` documentado: la arma y la lee el mismo formulario
  // (`ComerciosCondicionesFormComponent`, modo 'propuesta'), nunca a mano acá. `null` si todavía
  // no se cargaron.
  condiciones: any | null;
  efecto: { estado: 'NO_APLICA' | 'PENDIENTE' | 'HECHO' | 'FALLO'; detalle: EfectoDetalleAlta | null };
  // CONTRATO-PUESTA-EN-MARCHA.md, «B»: sólo corre tras aprobar. `null` hasta entonces — antes de
  // eso ni `app-puesta-en-marcha` ni la pestaña se muestran.
  puesta_en_marcha: PuestaEnMarcha | null;
  creada_por: string; created_at: string; updated_at: string; cerrada_at: string | null;
  acciones: Acciones;
  secciones_editables: Array<'DATOS' | 'CONTACTOS' | 'DOCUMENTOS' | 'CONDICIONES' | 'CUENTA'>;
  falta_para_aprobar: string[];   // p. ej. «Falta el contacto administrador del comercio.»
  documentos: DocumentoSolicitud[];   // TODOS los del tipo, en su orden, con lo cargado o null
  // Cambio de Juan (§5): los documentos `para: 'APROBAR'` NO frenan; si faltan, aprobar exige una
  // nota. `documentos_faltantes` es lo que falta HOY para el perfil de la etapa (si es el que carga
  // documentos; si no, `[]`). `nota_documentos` es la última nota dejada al aprobar sin ellos, o null.
  documentos_faltantes: Array<{ codigo: string; nombre: string }>;
  nota_documentos: NotaDocumentos | null;
}

/** Un documento ya cargado (lo que trae `Solicitud.documentos[].cargado`). */
export interface DocumentoCargado {
  nombre_archivo: string; url: string; mime: string; size: number;
  subido_por: { username: string }; subido_at: string;
}

/** Un renglón de `documentos`: qué es, para qué cuenta (crear o aprobar), quién lo sube y si ya
 *  está cargado. `la_llena` (contrato CONTRATO-ALTA-AUTOMATICA.md §1): el perfil que puede subir ESE
 *  documento — antes de esto los cinco documentos eran siempre del ejecutivo; ahora el contrato
 *  firmado (`CONTRATO_FIRMADO`) es del gerente comercial. */
export interface DocumentoSolicitud {
  codigo: string; nombre: string; para: 'CREAR' | 'APROBAR'; la_llena: string; cargado: DocumentoCargado | null;
}

/** El comercio que nació al ejecutar el alta (efecto `HECHO`, contrato §2). */
export interface ComercioDeAlta { _id: string; code: string; name: string; }

/** `efecto.detalle`: el comercio si el efecto quedó `HECHO`, o el error y el paso si quedó `FALLO`. */
export interface EfectoDetalleAlta { comercio?: ComercioDeAlta; error?: string; paso?: string; }

/** La nota dejada al aprobar con documentos `para: 'APROBAR'` faltantes (§5, cambio de Juan). */
export interface NotaDocumentos {
  texto: string; faltaban: Array<{ codigo: string; nombre: string }>;
  username: string; rol_nombre: string; etapa_orden: number; at: string;
}

export interface ResumenSolicitud {
  _id: string; numero: string; tipo_solicitud: string; estado: EstadoSolicitud;
  comercio: string | null; rif: string | null;
  etapa: { orden: number; rol_nombre: string; desde: string; devuelta_por: DevueltaPor | null } | null;
  etapas_total: number | null;        // cuántas etapas tiene su versión del workflow («2 de 4»)
  responsable: { username: string; desde: string } | null;
  actuo_como: string | null;          // con qué perfil le toca a quien mira, si le toca
  es_mia: boolean;                    // la tiene quien mira
  falta: string | null;               // lo primero que falta para que la etapa pueda aprobar (el contacto)
  documentos_faltan: number;          // cuántos documentos `para: 'APROBAR'` faltan (no frena; §5)
  efecto_estado: 'NO_APLICA' | 'PENDIENTE' | 'HECHO' | 'FALLO' | null;   // legacy: APROBADA + PENDIENTE = «alta pendiente»
  // CONTRATO-PUESTA-EN-MARCHA.md: manda sobre `efecto_estado` cuando no es null (solicitudes de
  // antes de este contrato no la tienen).
  puesta_estado: 'EN_CURSO' | 'OPERATIVA' | 'FALLO' | null;
  created_at: string; updated_at: string;
}

export interface Bandeja {
  me_tocan: ResumenSolicitud[]; por_tomar: ResumenSolicitud[]; de_otros: ResumenSolicitud[];
  tengo_perfil: boolean;   // ¿el usuario tiene algún perfil del workflow del alta?
  puede_crear: boolean;    // ¿tiene el primer perfil, el que crea las solicitudes?
}

export type TipoEvento = 'CREADA' | 'TOMADA' | 'SOLTADA' | 'COMENTARIO' | 'DATOS' | 'DOCUMENTO' | 'CONDICIONES' | 'APROBADA' | 'DEVUELTA' | 'RECHAZADA' | 'FINALIZADA' | 'ALTA' | 'PUESTA_EN_MARCHA';
export interface EventoSolicitud {
  _id: string; at: string; tipo: TipoEvento;
  etapa: { orden: number; rol_codigo: string; rol_nombre: string } | null;
  // `como_admin`/`actuo_como` (CONTRATO-ADMINISTRADOR.md): presentes cuando lo hizo el Administrador
  // de altas, actuando como el perfil de esa etapa. El historial dice «<usuario> (administrador)
  // aprobó como Gerente comercial».
  actor: { kind: 'PERFIL' | 'SISTEMA' | 'CLIENTE' | 'COMERCIO'; user?: string; username?: string; como_admin?: boolean; actuo_como?: string | null };
  comentario: string | null; motivo: string | null;
  data: any;   // APROBADA/DEVUELTA/RECHAZADA: { decision, destino: { orden, rol_nombre } | { tipo:'FIN'|'RECHAZADA' } } ·
               // DATOS: { campos: string[] } · DOCUMENTO: { tipo, nombre, nombre_archivo, reemplazo: boolean } ·
               // CONDICIONES: { plazo_dias, comision, modelos_usa: string[] } ·
               // APROBADA, si aprobó sin documentos (§5): además `documentos_faltantes: [codigos]` y `nota_documentos`
}

export interface MotivoRechazo { codigo: string; nombre: string; }

@Injectable({ providedIn: 'root' })
export class SolicitudesAltaService {
  private api = `${environment.BASE_LA}/api/workflows/solicitudes`;

  constructor(private http: HttpClient) { }

  private datos = async <T>(o: any): Promise<T> => (await lastValueFrom(o) as any).data as T;

  bandeja = () => this.datos<Bandeja>(this.http.get(`${this.api}/bandeja`));
  todas = (estado?: EstadoSolicitud) => this.datos<ResumenSolicitud[]>(this.http.get(this.api, { params: estado ? { estado, tipo: 'ALTA_COMERCIO' } : { tipo: 'ALTA_COMERCIO' } }));
  motivos = () => this.datos<MotivoRechazo[]>(this.http.get(`${this.api}/motivos`));
  obtener = (id: string) => this.datos<Solicitud>(this.http.get(`${this.api}/${id}`));
  historial = (id: string) => this.datos<EventoSolicitud[]>(this.http.get(`${this.api}/${id}/historial`));
  // multipart/form-data (contrato §3): tipo_solicitud + datos y contactos en JSON de texto + el
  // archivo del RIF. El servidor vuelve a validar todo; acá solo se arma el sobre.
  crear = (datos: DatosAlta, contactos: ContactoAlta[], rif: File) => {
    const fd = new FormData();
    fd.append('tipo_solicitud', 'ALTA_COMERCIO');
    fd.append('datos', JSON.stringify(datos));
    fd.append('contactos', JSON.stringify(contactos));
    fd.append('RIF', rif);
    return this.datos<Solicitud>(this.http.post(this.api, fd));
  };
  // multipart/form-data: campo `tipo` + el archivo `archivo`. Reemplaza el del mismo tipo si ya
  // había uno cargado. Devuelve `aPantalla` (la Solicitud entera, con `documentos` al día).
  subirDocumento = (id: string, tipo: string, archivo: File) => {
    const fd = new FormData();
    fd.append('tipo', tipo);
    fd.append('archivo', archivo);
    return this.datos<Solicitud>(this.http.post(`${this.api}/${id}/documentos`, fd));
  };
  /** Quita un documento cargado, para volver a cargarlo (mismos permisos que subirlo). */
  quitarDocumento = (id: string, tipo: string) => this.datos<Solicitud>(this.http.delete(`${this.api}/${id}/documentos/${tipo}`));
  centrosBeneficio = () => this.datos<CentroBeneficio[]>(this.http.get(`${this.api}/centros-beneficio`));
  /** Las `url` de los documentos son relativas cuando el núcleo guarda en disco (local, sin
   *  API_KEY de Cloudinary): hay que anteponerles el servidor. Si ya vienen absolutas, se dejan. */
  urlDocumento = (url: string): string => (url && url.startsWith('/')) ? `${environment.BASE_LA}${url}` : url;
  tomar = (id: string) => this.datos<Solicitud>(this.http.post(`${this.api}/${id}/tomar`, {}));
  soltar = (id: string) => this.datos<Solicitud>(this.http.post(`${this.api}/${id}/soltar`, {}));
  comentar = (id: string, texto: string) => this.datos<{ comentado: boolean }>(this.http.post(`${this.api}/${id}/comentarios`, { texto }));
  actualizar = (id: string, cambios: { datos?: DatosAlta; contactos?: ContactoAlta[] }) =>
    this.datos<Solicitud>(this.http.patch(`${this.api}/${id}`, cambios));
  // El cuerpo es el mismo que arma `ComerciosCondicionesFormComponent` en modo 'propuesta' (contrato
  // CONTRATO-CONDICIONES.md, «Las condiciones en el alta»): solo quien la tiene y cuyo perfil de
  // etapa llena la sección CONDICIONES. Devuelve `aPantalla` (la Solicitud con `condiciones` al día).
  guardarCondiciones = (id: string, cuerpo: any) =>
    this.datos<Solicitud>(this.http.put(`${this.api}/${id}/condiciones`, cuerpo));
  // `nota_documentos` (§5): obligatoria cuando se aprueba con documentos `para: 'APROBAR'` faltantes;
  // el servidor la exige con 400 si falta. Se manda solo cuando hay algo que escribir.
  decidir = (id: string, decision: Decision, comentario: string, motivo?: string, nota_documentos?: string, rige_desde?: string) =>
    this.datos<Solicitud>(this.http.post(`${this.api}/${id}/decision`, {
      decision, comentario,
      ...(motivo ? { motivo } : {}),
      ...(nota_documentos ? { nota_documentos } : {}),
      ...(rige_desde ? { rige_desde } : {}),
    }));
  // Aprobada con el alta en FALLO (contrato §2): solo con `acciones.reintentar_alta`. Vuelve a correr
  // lo que falte de `ejecutarAlta` sin duplicar lo ya creado. Devuelve `aPantalla` con el efecto al día.
  reintentarAlta = (id: string) => this.datos<Solicitud>(this.http.post(`${this.api}/${id}/alta/reintentar`, {}));
  // CONTRATO-PUESTA-EN-MARCHA.md, «B»: sólo con `acciones.reintentar_puesta_en_marcha`. Rehace SOLO
  // los pasos que quedaron en FALLO; devuelve `aPantalla` con `puesta_en_marcha` al día.
  reintentarPuestaEnMarcha = (id: string) =>
    this.datos<Solicitud>(this.http.post(`${this.api}/${id}/puesta-en-marcha/reintentar`, {}));
  // Cambio «E»: reintentar UN ítem puntual — rehace ese paso y los que dependen de él.
  reintentarPaso = (id: string, clave: string) =>
    this.datos<Solicitud>(this.http.post(`${this.api}/${id}/puesta-en-marcha/pasos/${clave}/reintentar`, {}));
  // Un paso que falló por un dato incompleto trae `completar` (hoy: FICHA con el domicilio fiscal sin calle o ciudad):
  // se guarda el dato y el núcleo reintenta. Devuelve la solicitud al día, igual que `reintentarPaso`.
  completarPuesta = (id: string, fiscal: DomicilioFiscal) =>
    this.datos<Solicitud>(this.http.post(`${this.api}/${id}/puesta-en-marcha/completar`, { fiscal }));
  // «En vivo»: el sondeo cada 500 ms mientras `estado === 'EN_CURSO'` (lo hace quien embebe
  // `app-puesta-en-marcha`, nunca el componente). Deliberadamente liviano: sólo el sub-objeto, no la
  // solicitud entera — un GET cada 500 ms con todo el expediente sería tirar datos a la basura.
  puestaEnMarcha = (id: string) => this.datos<PuestaEnMarcha>(this.http.get(`${this.api}/${id}/puesta-en-marcha`));

  /**
   * CONTRATO-PUESTA-EN-MARCHA.md, «K»: arma el PDF «Credenciales de <Comercio>» AL ENTREGAR — no
   * se guarda en ningún lado — y ROTA las claves: las que se entregaron antes dejan de servir. Va
   * por `HttpClient`, no por un `<a href>` pelado: la sesión viaja en el encabezado que pone
   * `AuthInterceptorService`, y un enlace directo del navegador no lo manda. `responseType: 'blob'`
   * + `observe: 'response'` para leer el nombre del `Content-Disposition`
   * (`credenciales-<CODIGO>.pdf`, contrato «K»).
   */
  /** `parte`: «acceso» (el portal, para el comercio) o «integracion» (la pasarela, uso interno de CrediTotal). */
  entregarCredenciales = async (id: string, parte: 'acceso' | 'integracion' = 'acceso'): Promise<{ blob: Blob; nombre: string }> => {
    let respuesta;
    try {
      respuesta = await lastValueFrom(this.http.post(`${this.api}/${id}/credenciales?parte=${parte}`, {}, {
        responseType: 'blob',
        observe: 'response',
      }));
    } catch (e: any) {
      throw await this.errorDeBlob(e);
    }
    const nombre = this.nombreDeArchivo(respuesta.headers.get('content-disposition')) || `credenciales-${parte}-${id}.pdf`;
    return { blob: respuesta.body as Blob, nombre };
  };

  /** Los usuarios del portal que nacieron con el alta (admin, gerente, cajero) y el estado de su clave. */
  accesos = (id: string) => this.datos<AccesosAlta>(this.http.get(`${this.api}/${id}/accesos`));

  /** Renueva la clave de UNA persona y se la manda por correo a ella; nadie de CrediTotal la ve. */
  renovarAcceso = (id: string, usuarioId: string) =>
    this.datos<{ ok: boolean; enviado_en: string | null; envio_estado: EnvioAcceso; envio_error: string | null }>(
      this.http.post(`${this.api}/${id}/accesos/${usuarioId}/renovar`, {}));

  /** `attachment; filename="credenciales-MT-01.pdf"` → `credenciales-MT-01.pdf`. */
  private nombreDeArchivo = (cabecera: string | null): string | null => {
    if (!cabecera) return null;
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecera);
    return m ? decodeURIComponent(m[1].trim()) : null;
  };

  /**
   * Con `responseType: 'blob'` el cuerpo del error TAMBIÉN es un blob: `mensajeDeError` (que lee
   * `e.error.error`/`e.error.msg` como JSON) se quedaría siempre en el texto por omisión. Se lee el
   * blob como texto acá y se rearma el error con la forma de siempre — para que la pantalla use el
   * mismo `mensajeDeError`/`this.fallo()` de las otras acciones, sin duplicar nada.
   */
  private errorDeBlob = async (e: any): Promise<any> => {
    if (!(e?.error instanceof Blob)) return e;
    try {
      return { ...e, error: JSON.parse(await (e.error as Blob).text()) };
    } catch {
      return e;
    }
  };
}

export const PAPELES: Array<{ codigo: PapelContacto; nombre: string }> = [
  { codigo: 'ADMINISTRADOR', nombre: 'Administrador' },
  { codigo: 'FINANZAS', nombre: 'Finanzas' },
  { codigo: 'TECNICO', nombre: 'Técnico' },
  { codigo: 'OTRO', nombre: 'Otro' },
];

/** Días enteros desde una fecha (para «días en la etapa»). */
export function diasDesde(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d) / 86400000));
}
